import React, { useState, useRef, useEffect } from 'react';
import { Button, TextField, Typography, Grid, Paper, Box } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Layout from './components/Layout';
import { calculateStats } from './utils/stats';
import StrikeZoneChart from './components/StrikeZoneChart';
import PitchesPerInningChart from './components/PitchesPerInningChart';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/Header';
import HistoryPanel from './components/HistoryPanel';
import SharePanel from './components/SharePanel';
import ExportLayout from './components/ExportLayout';
import SequenceChart from './components/SequenceChart';

const HIGHLIGHT_DURATION_MS = 1500;
const HIGHLIGHT_KEYS = [
  'strikes',
  'balls',
  'outs',
  'pitches',
  'innings',
  'runsAllowed',
  'strikeouts',
  'walks',
  'hits',
];
const createHighlightState = () =>
  HIGHLIGHT_KEYS.reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {});

const AppContent = () => {
  const { currentUser } = useAuth();
  const shareTargetRef = useRef(null);
  const exportTargetRef = useRef(null); // Ref for horizontal export
  const [pitches, setPitches] = useState(0);
  const [strikes, setStrikes] = useState(0);
  const [balls, setBalls] = useState(0);
  const [outs, setOuts] = useState(0);
  const [innings, setInnings] = useState(1); // イニング数
  const [runsAllowed, setRunsAllowed] = useState(0); // 失点
  const [pitcherName, setPitcherName] = useState(''); // 投手名
  const [opponent, setOpponent] = useState(''); // 対戦相手
  const [history, setHistory] = useState([]); // 操作履歴を保存するためのステート
  const [pitched, setPitched] = useState(0);
  const [strikeouts, setStrikeouts] = useState(0); // 三振
  const [walks, setWalks] = useState(0); // フォアボール
  const [hits, setHits] = useState(0); // 被安打
  const [savedPitchesPerInning, setSavedPitchesPerInning] = useState([]); // 保存されたイニング別投球数
  const [highlightedStats, setHighlightedStats] = useState(() => createHighlightState());
  const highlightTimersRef = useRef({});

  // 現在の日付を取得
  const today = new Date().toLocaleDateString();

  const createPressHandlers = (handler) => ({
    onPointerDown: (event) => {
      event.preventDefault();
      handler();
    },
    onKeyDown: (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handler();
      }
    },
  });

  useEffect(() => {
    const timersRef = highlightTimersRef.current;
    return () => {
      Object.values(timersRef).forEach((timerId) => {
        if (timerId) {
          clearTimeout(timerId);
        }
      });
    };
  }, []);

  const triggerHighlight = (keys = []) => {
    setHighlightedStats((prev) => {
      const nextState = { ...prev };
      keys.forEach((key) => {
        if (HIGHLIGHT_KEYS.includes(key)) {
          nextState[key] = true;
        }
      });
      return nextState;
    });

    keys.forEach((key) => {
      if (!HIGHLIGHT_KEYS.includes(key)) {
        return;
      }

      if (highlightTimersRef.current[key]) {
        clearTimeout(highlightTimersRef.current[key]);
      }

      highlightTimersRef.current[key] = setTimeout(() => {
        setHighlightedStats((prev) => ({ ...prev, [key]: false }));
        highlightTimersRef.current[key] = undefined;
      }, HIGHLIGHT_DURATION_MS);
    });
  };

  const renderTrackedStat = (key, label, value) => (
    <Typography data-testid={`stat-${key}`}>
      <span
        data-testid={`stat-${key}-content`}
        className={highlightedStats[key] ? 'stat-highlight' : undefined}
      >
        {label}: {value}
      </span>
    </Typography>
  );

  const handleStrike = () => {
    saveHistory();
    setStrikes((prev) => prev + 1);
    setPitches((prev) => prev + 1);
    setPitched(1);
    triggerHighlight(['strikes', 'pitches']);
  };

  const handleBall = () => {
    saveHistory();
    setBalls((prev) => prev + 1);
    setPitches((prev) => prev + 1);
    setPitched(1);
    triggerHighlight(['balls', 'pitches']);
  };

  const handleOut = () => {
    saveHistory();
    setOuts((prevOuts) => {
      const nextOuts = prevOuts + 1;
      if (nextOuts > 0 && nextOuts % 3 === 0) {
        setPitched(0);
      }
      return nextOuts;
    });
    triggerHighlight(['outs']);
  };

  const handleStrikeout = () => {
    saveHistory();
    setStrikeouts((prev) => prev + 1);
    setPitched(1);
    triggerHighlight(['strikeouts']);
  };

  const handleWalk = () => {
    saveHistory();
    setWalks((prev) => prev + 1);
    setPitched(1);
    triggerHighlight(['walks']);
  };

  const handleHit = () => {
    saveHistory();
    setHits((prev) => prev + 1);
    setPitched(1);
    triggerHighlight(['hits']);
  };

  const handleInnings = () => {
    saveHistory();
    setInnings((prev) => prev + 1);
    triggerHighlight(['innings']);
  };

  const handleRun = () => {
    saveHistory();
    setRunsAllowed((prev) => prev + 1); // 失点追加
    triggerHighlight(['runsAllowed']);
  };

  const resetAll = () => {
    saveHistory();
    setPitches(0);
    setStrikes(0);
    setBalls(0);
    setOuts(0);
    setInnings(1);
    setRunsAllowed(0);
    setPitcherName('');
    setOpponent('');
    setPitched(0);
    setStrikeouts(0);
    setWalks(0);
    setHits(0);
    setHistory([]);
    setSavedPitchesPerInning([]);
  };

  const undoLastAction = () => {
    if (history.length > 0) {
      const lastState = history[history.length - 1];
      setHistory(history.slice(0, -1));
      setPitches(lastState.pitches);
      setStrikes(lastState.strikes);
      setBalls(lastState.balls);
      setOuts(lastState.outs);
      setInnings(lastState.innings);
      setRunsAllowed(lastState.runsAllowed);
      setPitcherName(lastState.pitcherName);
      setOpponent(lastState.opponent);
      setStrikeouts(lastState.strikeouts || 0);
      setWalks(lastState.walks || 0);
      setHits(lastState.hits || 0);
    }
  };

  const loadGameData = (gameData) => {
    setPitches(gameData.pitches || 0);
    setStrikes(gameData.strikes || 0);
    setBalls(gameData.balls || 0);
    setOuts(gameData.outs || 0);
    setInnings(gameData.innings || 1);
    setRunsAllowed(gameData.runsAllowed || 0);
    setPitcherName(gameData.pitcherName || '');
    setOpponent(gameData.opponent || '');
    setStrikeouts(gameData.strikeouts || 0);
    setWalks(gameData.walks || 0);
    setHits(gameData.hits || 0);
    setHistory(Array.isArray(gameData.pitchHistory) ? gameData.pitchHistory : []);
    setSavedPitchesPerInning(gameData.pitchesPerInning || []);
  };

  const saveHistory = () => {
    setHistory((prevHistory) => [...prevHistory, {
      pitches,
      strikes,
      balls,
      outs,
      innings,
      runsAllowed,
      pitcherName,
      opponent,
      runsPerInning,
      pitched,
      strikeouts,
      walks,
      hits
    }]);
  };

  const strikeRate = pitches > 0 ? ((strikes / pitches) * 100).toFixed(1) : 0;
  const outRate = outs > 0 ? (pitches / outs).toFixed(1) : 0;
  const runsPerInning = (innings > 0) ? (runsAllowed / innings).toFixed(2) : 0; // 失点/イニング数

  // Calculate extended stats
  const inningsPitched = outs / 3;
  const stats = calculateStats({
    inningsPitched,
    strikeouts,
    walks,
    hits,
    runsAllowed,
    pitches
  });

  const getPitchesPerInning = () => {
    if (history.length === 0) return [];

    // Get unique innings recorded
    const inningMap = {};
    history.forEach(h => {
      if (!inningMap[h.innings]) {
        inningMap[h.innings] = 0;
      }
      if (h.pitches > inningMap[h.innings]) {
        inningMap[h.innings] = h.pitches;
      }
    });

    // Add current state
    if (!inningMap[innings]) {
      inningMap[innings] = 0;
    }
    if (pitches > inningMap[innings]) {
      inningMap[innings] = pitches;
    }

    // Now convert cumulative to per-inning
    const result = [];
    let previousTotal = 0;
    // Sort innings
    const sortedInnings = Object.keys(inningMap).map(Number).sort((a, b) => a - b);

    sortedInnings.forEach(ing => {
      const currentTotal = inningMap[ing];
      const count = currentTotal - previousTotal;
      if (count > 0) {
        result.push({ inning: ing, pitches: count });
      }
      previousTotal = currentTotal;
    });

    return result;
  };

  const pitchesPerInningData = savedPitchesPerInning.length > 0 ? savedPitchesPerInning : getPitchesPerInning();
  const gameSnapshot = {
    date: today,
    pitcherName,
    opponent,
    pitches,
    strikes,
    balls,
    outs,
    innings,
    runsAllowed,
    strikeouts,
    walks,
    hits,
    stats,
    strikeRate,
    outRate,
    runsPerInning,
    pitchesPerInning: pitchesPerInningData,
    pitchHistory: [...history],
  };

  return (
    <Layout>
      <Header />
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
        <Box
          ref={shareTargetRef}
          sx={{
            p: { xs: 2, md: 3 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="h4" gutterBottom>
            Pitch Counter
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            {today}
          </Typography>

          {/* Pitcher Name and Opponent Field */}
          <Grid container spacing={2} direction="column" style={{ marginBottom: '20px' }}>
            <Grid item xs={12}>
              <TextField
                label="Pitcher Name"
                value={pitcherName}
                onChange={(e) => setPitcherName(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Opponent"
                value={opponent}
                onChange={(e) => setOpponent(e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>

          {/* Statistics */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {renderTrackedStat('strikes', 'Strikes', strikes)}
              {renderTrackedStat('balls', 'Balls', balls)}
              {renderTrackedStat('outs', 'Outs', outs)}
            </Grid>
            <Grid item xs={6}>
              {renderTrackedStat('pitches', 'Pitches', pitches)}
              {renderTrackedStat('innings', 'Innings', innings)}
              {renderTrackedStat('runsAllowed', 'Runs Allowed', runsAllowed)}
            </Grid>
            <Grid item xs={4}>
              {renderTrackedStat('strikeouts', 'K', strikeouts)}
            </Grid>
            <Grid item xs={4}>
              {renderTrackedStat('walks', 'BB', walks)}
            </Grid>
            <Grid item xs={4}>
              {renderTrackedStat('hits', 'H', hits)}
            </Grid>
          </Grid>

          {/* Buttons */}
          <Grid container spacing={1} style={{ marginTop: '10px' }}>
            <Grid item xs={2}>
              <Button
                variant="contained"
                disableRipple
                disableTouchRipple
                style={{
                  backgroundColor: 'yellow',
                  color: 'black',
                  borderRadius: '50%',
                  minWidth: '45px',
                  height: '45px',
                  padding: '0px'
                }}
                {...createPressHandlers(handleStrike)}
              >
                S
              </Button>
            </Grid>
            <Grid item xs={2}>
              <Button
                variant="contained"
                disableRipple
                disableTouchRipple
                style={{
                  backgroundColor: 'green',
                  color: 'white',
                  borderRadius: '50%',
                  minWidth: '45px',
                  height: '45px',
                  padding: '0px'
                }}
                {...createPressHandlers(handleBall)}
              >
                B
              </Button>
            </Grid>
            <Grid item xs={2}>
              <Button
                variant="contained"
                disableRipple
                disableTouchRipple
                style={{
                  backgroundColor: 'red',
                  color: 'white',
                  borderRadius: '50%',
                  minWidth: '45px',
                  height: '45px',
                  padding: '0px'
                }}
                {...createPressHandlers(handleOut)}
              >
                O
              </Button>
            </Grid>
            <Grid item xs={2}>
              <Button
                variant="contained"
                disableRipple
                disableTouchRipple
                style={{
                  backgroundColor: 'blue',
                  color: 'white',
                  minWidth: '45px',
                  height: '45px',
                  padding: '0px'
                }}
                {...createPressHandlers(handleStrikeout)}
              >
                K
              </Button>
            </Grid>
            <Grid item xs={2}>
              <Button
                variant="contained"
                disableRipple
                disableTouchRipple
                style={{
                  backgroundColor: 'purple',
                  color: 'white',
                  minWidth: '45px',
                  height: '45px',
                  padding: '0px'
                }}
                {...createPressHandlers(handleWalk)}
              >
                FB
              </Button>
            </Grid>
            <Grid item xs={2}>
              <Button
                variant="contained"
                disableRipple
                disableTouchRipple
                color="secondary"
                style={{
                  minWidth: '45px',
                  height: '45px',
                  padding: '0px'
                }}
                {...createPressHandlers(handleHit)}
              >
                H
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={2} style={{ marginTop: '10px' }}>
            <Grid item xs={6}>
              <Button variant="outlined" color="error" disableRipple disableTouchRipple {...createPressHandlers(handleRun)}>
                Run
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button variant="outlined" color="warning" disableRipple disableTouchRipple {...createPressHandlers(handleInnings)}>
                Innings
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={2} style={{ marginTop: '10px' }}>
            <Grid item xs={6}>
              <Button variant="outlined" startIcon={<UndoIcon />} disableRipple disableTouchRipple {...createPressHandlers(undoLastAction)}>
                Undo
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button variant="outlined" startIcon={<RestartAltIcon />} disableRipple disableTouchRipple {...createPressHandlers(resetAll)}>
                Reset
              </Button>
            </Grid>
          </Grid>

          {/* Rates */}
          <Typography variant="body1" style={{ marginTop: '20px' }}>
            Strike Rate: {strikeRate}%
          </Typography>
          <Typography variant="body1">
            Outs Rate: {outRate} pitches/out
          </Typography>
          <Typography variant="body1">
            Runs per Inning: {runsPerInning}
          </Typography>
          <Typography variant="body1" style={{ marginTop: '10px', fontWeight: 'bold' }}>
            Advanced Stats:
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <Typography variant="caption">K/9: {stats.k9}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption">BB/9: {stats.bb9}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption">K/BB: {stats.kbb}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption">WHIP: {stats.whip}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption">P/IP: {stats.pip}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption">RA: {stats.ra}</Typography>
            </Grid>
          </Grid>

          {/* Charts */}
          <Typography variant="body1" style={{ marginTop: '20px', fontWeight: 'bold' }}>
            Visualizations:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)' }}>
                <Typography variant="caption" align="center" display="block" sx={{ mb: 1 }}>
                  Sequence (Pitch-by-Pitch)
                </Typography>
                <SequenceChart history={history} current={gameSnapshot} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)' }}>
                <Typography variant="caption" align="center" display="block" sx={{ mb: 1 }}>
                  Pitches per Inning
                </Typography>
                <PitchesPerInningChart data={pitchesPerInningData} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)' }}>
                <Typography variant="caption" align="center" display="block" sx={{ mb: 1 }}>
                  Ratio (S/B/H)
                </Typography>
                <StrikeZoneChart strikes={strikes} balls={balls} hits={hits} />
              </Box>
            </Grid>
          </Grid>
        </Box>
        <SharePanel snapshot={gameSnapshot} shareTargetRef={exportTargetRef} />
        <HistoryPanel user={currentUser} gameSnapshot={gameSnapshot} onLoadGame={loadGameData} />

        {/* Hidden Export Layout */}
        <ExportLayout
          ref={exportTargetRef}
          snapshot={gameSnapshot}
          history={history}
          pitchesPerInningData={pitchesPerInningData}
        />
      </Paper>
    </Layout>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
